import { useState, useEffect, useCallback } from 'react'
import { Contract } from 'ethers'
import { getChainProvider } from '@/lib/chainProvider'
import { VIEWS_CONTRACT_ADDRESS, GAME_CHAIN_ID } from '@/lib/contracts'
import { RaffleMiningViewsABI } from '@/abi/RaffleMiningViewsABI'

export interface LeaderboardEntry {
  rank: number
  address: string
  ethSpent: bigint
  roundsParticipated: bigint
  roundsWon: bigint
  ethWon: bigint
  welephantWon: bigint
}

export type SortField = 'rank' | 'ethWon' | 'welephantWon' | 'roundsParticipated'
export type SortDirection = 'asc' | 'desc'

interface UseLeaderboardResult {
  entries: LeaderboardEntry[]
  loading: boolean
  error: string | null
  sortField: SortField
  sortDirection: SortDirection
  setSort: (field: SortField) => void
  refresh: () => void
}

export function useLeaderboard(enabled: boolean = true): UseLeaderboardResult {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('rank')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const fetchLeaderboard = useCallback(async () => {
    if (!enabled) {
      setLoading(false)
      return
    }
    if (!VIEWS_CONTRACT_ADDRESS) {
      setError('Views contract not configured')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const provider = await getChainProvider(GAME_CHAIN_ID)
      const viewsContract = new Contract(
        VIEWS_CONTRACT_ADDRESS,
        RaffleMiningViewsABI,
        provider
      )

      // Fetch top 100 players
      const [players, stats] = await viewsContract.getTopPlayersStats(100n)

      // Map to LeaderboardEntry format
      const leaderboardEntries: LeaderboardEntry[] = players.map(
        (address: string, index: number) => ({
          rank: index + 1,
          address,
          ethSpent: stats[index].ethSpent,
          roundsParticipated: stats[index].roundsParticipated,
          roundsWon: stats[index].roundsWon,
          ethWon: stats[index].ethWon,
          welephantWon: stats[index].welephantWon,
        })
      )

      setEntries(leaderboardEntries)
    } catch (err) {
      console.error('[useLeaderboard] Error fetching leaderboard:', err)
      setError('Failed to fetch leaderboard data')
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    fetchLeaderboard()
  }, [fetchLeaderboard])

  // Sort entries when sort field/direction changes
  const sortedEntries = [...entries].sort((a, b) => {
    let comparison = 0

    switch (sortField) {
      case 'rank':
        comparison = a.rank - b.rank
        break
      case 'ethWon':
        comparison = a.ethWon > b.ethWon ? 1 : a.ethWon < b.ethWon ? -1 : 0
        break
      case 'welephantWon':
        comparison = a.welephantWon > b.welephantWon ? 1 : a.welephantWon < b.welephantWon ? -1 : 0
        break
      case 'roundsParticipated':
        comparison = a.roundsParticipated > b.roundsParticipated ? 1 : a.roundsParticipated < b.roundsParticipated ? -1 : 0
        break
    }

    return sortDirection === 'asc' ? comparison : -comparison
  })

  const setSort = useCallback((field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // Default to descending for value fields, ascending for rank
      setSortField(field)
      setSortDirection(field === 'rank' ? 'asc' : 'desc')
    }
  }, [sortField])

  return {
    entries: sortedEntries,
    loading,
    error,
    sortField,
    sortDirection,
    setSort,
    refresh: fetchLeaderboard,
  }
}
